import { ApiTransaction, Transaction } from './transactionTypes'
import { transactionUtils } from '@/utils/transactionUtils'
import { currencyUtils } from '@/utils/currency'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'

/**
 * Additional properties for a formatted transaction (adapted from RN)
 */
export interface FormattedTransaction extends Transaction {
  // Formatted values
  formattedRate?: string
  formattedNetwork?: {
    name?: string
    imageUrl?: string
  }
  formattedDepositAddress?: string
  formattedAmount?: string
  formattedFiatAmount?: string
  formattedCreditAmount?: string
  formattedFiatCreditAmount?: string
  formattedFee?: string
  hasFee?: boolean
  formattedCardFee?: string
  formattedPaymentAmount?: string
  formattedDate?: string
  formattedTime?: string
}

/**
 * Transform API transaction to formatted domain transaction
 */
export function adapterTransaction(
  apiTransaction: ApiTransaction,
): FormattedTransaction {
  // Base transformation (dates)
  const baseTransaction: Transaction = {
    ...apiTransaction,
    created_at: apiTransaction.created_at
      ? new Date(apiTransaction.created_at * 1000)
      : new Date(),
    updated_at: apiTransaction.updated_at
      ? new Date(apiTransaction.updated_at * 1000)
      : new Date(),
  }

  const { isWithdrew } =
    transactionUtils.getTransactionConditionalsToRenderLayouts({
      direction: apiTransaction.direction,
      type: apiTransaction.type,
      state: apiTransaction.state,
    })

  // Format amounts
  const formattedAmount = formatCryptoAmount(
    apiTransaction.amount.amount,
    apiTransaction.amount.currency,
  )

  const formattedFiatAmount = formatFiatAmount(
    apiTransaction.amount.fiat_amount,
    apiTransaction.amount.fiat_currency,
  )

  // Format credit amount for swaps
  const formattedCreditAmount = apiTransaction.subject_swap?.credit_amount
    ? formatCryptoAmount(
        apiTransaction.subject_swap.credit_amount.amount,
        apiTransaction.subject_swap.credit_amount.currency,
      )
    : undefined

  const formattedFiatCreditAmount = apiTransaction.subject_swap?.credit_amount
    ? formatFiatAmount(
        apiTransaction.subject_swap.credit_amount.fiat_amount,
        apiTransaction.subject_swap.credit_amount.fiat_currency,
      )
    : undefined

  // Format fee
  const formattedFee = formatCryptoAmount(
    apiTransaction.fee_amount.amount,
    apiTransaction.fee_amount.currency,
  )

  const hasFee = () => {
    const amount = Number(apiTransaction.fee_amount.amount)
    return !Number.isNaN(amount) && amount !== 0
  }

  // Format card fee
  const formattedCardFee = apiTransaction.subject_card?.card_fee
    ? formatFiatAmount(
        apiTransaction.subject_card.card_fee.toString(),
        apiTransaction.subject_card.card_fee_currency,
      )
    : undefined

  // Format payment amount for top-ups
  const formattedPaymentAmount = apiTransaction.subject_top_up?.payment_amount
    ? formatFiatAmount(
        apiTransaction.subject_top_up.payment_amount.toString(),
        apiTransaction.subject_top_up.payment_currency,
      )
    : undefined

  // Format network information for deposits/withdrawals
  const formattedNetwork =
    apiTransaction.subject_deposit || apiTransaction.subject_withdraw
      ? {
          name: `${
            isWithdrew
              ? apiTransaction.subject_withdraw?.blockchain_name
              : apiTransaction.subject_deposit?.blockchain_name
          } Network`,
          imageUrl: isWithdrew
            ? apiTransaction.subject_withdraw?.blockchain_img_url
            : apiTransaction.subject_deposit?.blockchain_img_url,
        }
      : undefined

  // Format deposit/withdraw address
  const formattedDepositAddress = isWithdrew
    ? apiTransaction.subject_withdraw?.recipient_wallet_address
    : apiTransaction.subject_deposit?.payer_wallet_address

  // Format exchange rate for conversions
  const formattedRate = apiTransaction.subject_swap?.credit_amount
    ? `1 ${getDisplayCurrencyCode(apiTransaction.amount.currency)} = ${getRate(apiTransaction)}`
    : undefined

  // Format dates
  const transactionDate = apiTransaction.created_at
    ? new Date(apiTransaction.created_at * 1000)
    : new Date()

  const formattedDate = transactionUtils.formatDateByDeviceRegion({
    date: transactionDate,
    isTitle: true,
  })

  const formattedTime = transactionUtils.formatDateByDeviceRegion({
    date: transactionDate,
    isTitle: true,
    showTime: true,
  })

  return {
    ...baseTransaction,
    formattedRate,
    formattedNetwork,
    formattedDepositAddress,
    formattedAmount,
    formattedFiatAmount,
    formattedCreditAmount,
    formattedFiatCreditAmount,
    formattedFee,
    hasFee: hasFee(),
    formattedCardFee,
    formattedPaymentAmount,
    formattedDate,
    formattedTime,
  }
}

/**
 * Format crypto amount with proper decimals
 */
function formatCryptoAmount(amount: string, currency: string): string {
  const numAmount = parseFloat(amount)
  if (isNaN(numAmount)) return `0 ${currency === 'XAUT' ? 'GOLD' : currency}`

  // Use the currency utils for consistent formatting
  const formatted = currencyUtils.formatDynamicCurrency({
    amount: numAmount,
    currencyCode: currency,
    showDecimals: true,
  })

  // formatDynamicCurrency already includes the currency code, so don't duplicate it
  return formatted
}

/**
 * Format fiat amount with currency symbol using user's display currency
 */
function formatFiatAmount(amount: string, currency: string): string {
  const numAmount = parseFloat(amount)
  if (isNaN(numAmount)) return '$0.00'

  // Get user's display currency preference
  const displayCurrency = useAppPreferencesStore.getState().displayCurrency
  const userCurrency = displayCurrency?.isin || 'USD'

  // If the transaction currency matches user's display currency, use it
  // Otherwise, convert or use the transaction currency
  const currencyToUse = currency || userCurrency

  // Use the currency utils for consistent formatting
  return currencyUtils.formatFiatCurrency({
    value: numAmount,
    fiatCode: currencyToUse,
    showDecimals: true,
  })
}

/**
 * Get display currency code (simplified version)
 */
function getDisplayCurrencyCode(currencyCode: string): string {
  // This could be enhanced to handle special display codes
  return currencyCode
}

/**
 * Calculate exchange rate for conversions
 */
function getRate(transaction: ApiTransaction): string {
  if (!transaction.subject_swap?.credit_amount) return '0'

  const fromAmount = parseFloat(transaction.amount.amount)
  const toAmount = parseFloat(transaction.subject_swap.credit_amount.amount)

  if (fromAmount === 0) return '0'

  const rate = toAmount / fromAmount
  return rate.toFixed(8).replace(/\.?0+$/, '')
}
